import { Bell, BellOff, BellRing } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { usePushNotifications } from "@/hooks/usePushNotifications";

/**
 * Compact toggle that asks for notification permission and (when a VAPID
 * public key is wired in) registers a push subscription. Without a backend
 * VAPID setup it falls back to in-page notifications via `showLocal()` so
 * the user can still verify their permission is working.
 *
 * Drop this anywhere — the More sheet, Settings page, or onboarding.
 */
export function EnableNotificationsButton() {
  const { supported, permission, isSubscribed, request, showLocal } =
    usePushNotifications();

  if (!supported) {
    return (
      <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
        <BellOff className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <span>
          Notifications aren't available in this context. Install Smplify to your
          home screen and reopen it to enable them.
        </span>
      </div>
    );
  }

  if (permission === "denied") {
    return (
      <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
        <BellOff className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <span>
          Notifications are blocked in your browser settings. Re-enable them for
          this site to receive alerts.
        </span>
      </div>
    );
  }

  const handleEnable = async () => {
    const result = await request();
    if (result === "granted") {
      await showLocal(
        "Notifications enabled",
        "You'll get alerts here when something needs your attention.",
        "/"
      );
      toast.success("Notifications enabled");
    } else if (result === "denied") {
      toast.error("Notifications blocked");
    }
  };

  const handleTest = async () => {
    const ok = await showLocal(
      "Smplify test alert",
      "This is what a Smplify notification looks like.",
      "/devices"
    );
    if (!ok) toast.error("Couldn't show notification");
  };

  if (permission === "granted") {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-border bg-card p-3">
        <BellRing className="h-5 w-5 text-success" aria-hidden />
        <div className="flex-1 text-sm">
          <p className="font-medium text-foreground">Notifications on</p>
          <p className="text-xs text-muted-foreground">
            {isSubscribed ? "Subscribed to push alerts." : "In-app alerts only."}
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={handleTest}>
          Test
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 rounded-lg border border-border bg-card p-3">
      <Bell className="h-5 w-5 text-muted-foreground" aria-hidden />
      <div className="flex-1 text-sm">
        <p className="font-medium text-foreground">Enable notifications</p>
        <p className="text-xs text-muted-foreground">
          Get alerted when devices change state or jobs complete.
        </p>
      </div>
      <Button size="sm" onClick={handleEnable}>
        Enable
      </Button>
    </div>
  );
}

export default EnableNotificationsButton;
